"use client"
import { useState, useEffect, useMemo, useRef } from "react"
import ProductModel from "@src/models/product"
import { InventoryAttributeSelector } from "@src/types/common"
import InventoryModel from "@src/models/inventory"
import useMutation from "@src/hooks/use-mutation"
import { Toastify } from "@src/components"
import { useSearchParams } from "next/navigation"
import { store } from "@src/store/store"
import { setBehavior } from "@src/store/slices/behaviorSlice"
import { syncCartItemCount } from "@src/utils/cart-count"
import { resolvePromotionPrice } from "@src/utils/promotion-price-resolver"

const useInventoryProduct = (
  product: ProductModel,
  updateApiAfterChange = true,
  updateUrl = false
) => {
  const searchParams = useSearchParams()

  const [productData, setProductData] = useState<ProductModel>(() => {
    return product ? ProductModel.fromJson(product) || product : undefined
  })
  const [firstAttributeOptions, setFirstAttributeOptions] =
    useState<InventoryAttributeSelector>(productData?.firstAttributeOptions)
  const [secondAttributeOptions, setSecondAttributeOptions] =
    useState<InventoryAttributeSelector>(productData?.secondAttributeOptions)
  const [count, setCount] = useState<number>(0)
  const [currentInventory, setCurrentInventory] = useState<InventoryModel>()

  const initialBasePrice = Number(product?.original_price || product?.price || 0)
  const [promotionPrice, setPromotionPrice] = useState<number | null>(() =>
    resolvePromotionPrice(initialBasePrice, product?.promotion_price, product)
  )
  const [originalPrice, setOriginalPrice] = useState<number>(initialBasePrice)

  const storeRef = useRef(store)

  const handleUpdateInventory = async (
    inventories: InventoryModel[],
    targetProduct?: ProductModel | any
  ) => {
    const activeProd = targetProduct || productData || product
    const inventory = inventories?.length ? inventories[0] : null
    setCurrentInventory(inventory)
    setCount(inventory?.stock_quantity > 0 ? 1 : 0)

    const basePrice = Number(
      inventory?.price || activeProd?.original_price || activeProd?.price || 0
    )
    if (basePrice > 0) {
      setOriginalPrice(basePrice)
    }

    const resolvedPromo = resolvePromotionPrice(
      basePrice,
      inventory?.promotion_price,
      activeProd
    )
    setPromotionPrice(resolvedPromo)

    storeRef.current.dispatch(setBehavior({ inventory_id: inventory?.id }))
    if (inventory && updateUrl) {
      const params = new URLSearchParams(searchParams.toString())
      params.set("inventory_id", inventory.id.toString())
      window.history.pushState(null, "", `?${params.toString()}`)
    }
  }

  useEffect(() => {
    if (product) {
      const parsed = ProductModel.fromJson(product) || product
      setProductData(parsed)
      if (Array.isArray(parsed?.inventories) && parsed.inventories.length > 0) {
        setFirstAttributeOptions(parsed.firstAttributeOptions)
        setSecondAttributeOptions(parsed.secondAttributeOptions)
        handleUpdateInventory(parsed.inventories, parsed)
      } else {
        const basePrice = Number(parsed.original_price || parsed.price || 0)
        if (basePrice > 0) {
          setOriginalPrice(basePrice)
        }
        setPromotionPrice(
          resolvePromotionPrice(basePrice, parsed.promotion_price, parsed)
        )
      }
    }
  }, [product])

  const { mutation, loadingWithKey } = useMutation()

  const handleChangeFirstAttribute = async value => {
    const inventories = productData?.getInventoriesByFirstAttribute
      ? productData.getInventoriesByFirstAttribute(value)
      : []
    handleUpdateInventory(inventories, productData)
  }
  const handleChangeSecondAttribute = async value => {
    const inventories = productData?.getInventoriesByAttributes
      ? productData.getInventoriesByAttributes([
          currentInventory?.firstAttribute?.value,
          value
        ])
      : []
    handleUpdateInventory(inventories, productData)
  }

  const handleAddToCart = async ({
    showToast = true,
    onSuccess = () => {},
    loadingKey = "addCart"
  }) => {
    if (currentInventory && updateApiAfterChange) {
      await mutation({
        url: "/auth/cart/add",
        method: "POST",
        params: {
          inventory_id: currentInventory.id,
          quantity: 1
        },
        config: {
          deviceAuthenticated: true
        },
        loadingKey,
        onSuccess: res => {
          syncCartItemCount(res)
          if (showToast) Toastify.success("Đã thêm vào giỏ hàng!")
          onSuccess?.()
        }
      })
    }
  }

  const handleUpdateQuantity = async (value: number) => {
    setCount(value)
  }

  return {
    onUpdateQuantity: handleUpdateQuantity,
    firstAttributeOptions,
    secondAttributeOptions,
    handleChangeFirstAttribute,
    handleChangeSecondAttribute,
    currentInventory,
    handleAddToCart,
    count,
    loadingWithKey,
    promotionPrice,
    originalPrice
  }
}

export default useInventoryProduct
